import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Flex,
  Table,
  CollapsibleFilterBuilder
} from 'onefaceui-react-components';
import { useDispatch } from 'react-redux';

import { getResourceTo } from 'react/utils/utils';
import { useDriverFields } from 'react/queries/fields/DriverFields';
import { getCriteriaChain } from 'react/utils/criteriaChainUtils';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { getDriverAndHelpersAssignments } from 'react/api/selectors/quickAssignment';
import {
  assignDriverToRoute,
  assignHelperToRoute,
  driverCanBeAssigned
} from './utils';

import {
  addIntervalFields,
  getDefaultFilters,
  mapFieldsToFilterBuilder,
  saveFiltersByColumn
} from '../utils';
import Loading from 'react/components/Loading';
import { Container } from './DriverListStyles';

function DriverList() {
  const FILTER_NAME = 'SpeedAssignmentDriver';
  const [data, setData] = useState([]);
  const { defaultSelected, defaultValues } = getDefaultFilters(FILTER_NAME);
  const driverFieldsQuery = useDriverFields();
  const filtersRef = useRef(defaultValues || {});
  const filterBuilderFieldsRef = useRef([]);
  const tableRef = useRef(null);
  const dispatch = useDispatch();

  const filterBuilderFields = useMemo(() => {
    const fieldsData = driverFieldsQuery.data || [];

    const fieldsMapped = fieldsData.map((field) =>
      mapFieldsToFilterBuilder(field, fieldsData)
    );

    return addIntervalFields(fieldsMapped);
  }, [driverFieldsQuery.data]);

  const driverTableColumns = [
    {
      title: getResourceTo('driver.key'),
      field: 'key',
      width: 75
    },
    {
      title: getResourceTo('speedassignment.drivergrid.name'),
      field: 'name',
      width: 100
    },
    {
      title: getResourceTo('speedassignment.drivergrid.jobtitle'),
      field: 'driverType',
      width: 75
    },
    {
      title: getResourceTo('Organization'),
      field: 'organization.key',
      width: 80
    },
    {
      field: 'assignedToNotCompletedRoute',
      title: 'five',
      width: 30,
      minWidth: 30,
      titleFormatter: 'tickCross',
      titleFormatterParams: {
        crossElement: '<div class="assignedToNotCompletedRoute" />'
      },
      formatter: 'tickCross',
      formatterParams: {
        allowEmpty: true,
        allowTruthy: true,
        tickElement: '<div class="gridCheckboxFalse"/>',
        crossElement: '<div class="gridCheckboxTrue" />'
      },
      headerSort: false
    },
    {
      field: 'isHelper',
      visible: false,
      formatter: 'tickCross',
      formatterParams: {
        allowEmpty: true,
        allowTruthy: true,
        tickElement: '<div class="gridCheckboxTrue" />',
        crossElement: ''
      }
    }
  ];

  const columnsPreferences = [
    {
      field: 'key'
    },
    {
      field: 'name'
    },
    {
      field: 'driverType'
    },
    {
      field: 'organization.key'
    },
    {
      field: 'assignedToNotCompletedRoute'
    }
  ];

  useEffect(() => {
    if (driverFieldsQuery.isSuccess && driverFieldsQuery.data) {
      const fieldsData = driverFieldsQuery.data || [];

      const fieldsMapped = fieldsData.map((field) =>
        mapFieldsToFilterBuilder(field, fieldsData)
      );
      const fields = addIntervalFields(fieldsMapped);

      filterBuilderFieldsRef.current = fields;
    }
  }, [driverFieldsQuery]);

  const requestDataTable = useCallback(
    async (pageParam = 1, filtersOverride = null) => {
      if (tableRef?.current) {
        const criteria = {
          filters: [
            'id',
            'key',
            'name',
            'driverType',
            'organization.key',
            'assignedToNotCompletedRoute'
          ],
          firstResult: pageParam - 1 === 0 ? 0 : (pageParam - 1) * 51,
          maxResults: 51
        };

        const sort = [
          {
            attr: 'name',
            type: 'ASC'
          }
        ];

        const activeFilters = filtersOverride || filtersRef.current;
        const currentCriteriaChain = getCriteriaChain(
          activeFilters,
          filterBuilderFieldsRef.current,
          true,
          sort
        );

        const url = `/Driver/FindAssignmentAvailability?criteria=${encodeURIComponent(
          JSON.stringify(criteria)
        )}`;

        tableRef.current?.setLoading(true);
        const response = await defaultFetch(url, currentCriteriaChain, 'POST');
        tableRef.current?.setLoading(false);

        if (response.error) {
          return [];
        }

        return response.map(({ driver, assignedToNotCompletedRoute }) => ({
          ...driver,
          assignedToNotCompletedRoute,
          isHelper: driver.driverType !== 'DEFAULT' ? true : ''
        }));
      }
      return [];
    },
    [tableRef, filtersRef, filterBuilderFieldsRef]
  );

  const handleFilterSubmit = async (values) => {
    filtersRef.current = values;

    const mappedData = await requestDataTable(1, values);
    if (mappedData) {
      setData(mappedData);
      saveFiltersByColumn(FILTER_NAME, values);
    }
  };

  const handleDropDriver = useCallback(
    (driver, routeId) => {
      const routes = GreenmileReduxStore.getState()
        .quickAssignment.get('routes')
        .toJS();
      const route = routes.find((item) => item.id === Number(routeId));

      if (!route) {
        return;
      }

      const blockedToAdd =
        route.status === 'DEPARTED_ORIGIN' &&
        driver.assignedToNotCompletedRoute;

      if (blockedToAdd) {
        return;
      }

      const drivers = getDriverAndHelpersAssignments(
        GreenmileReduxStore.getState(),
        route.id
      ).map((item) => item.driver ?? item.helper);

      const canAssign = driverCanBeAssigned(driver, drivers);

      if (!canAssign) {
        return;
      }

      if (driver.driverType === 'DEFAULT') {
        assignDriverToRoute(driver, route.id, dispatch);
      }

      if (driver.driverType === 'HELPER') {
        assignHelperToRoute(driver, route.id, dispatch);
      }
    },
    [window.globalStore]
  );

  return (
    <Container data-testid="driver-list-container">
      <CollapsibleFilterBuilder
        defaultExpanded
        sectionTitleLabel={getResourceTo('speedassignment.driverlist')}
        dataTestId="filter-builder"
        defaultValues={defaultValues}
        defaultSelected={defaultSelected}
        onSubmit={handleFilterSubmit}
        fields={filterBuilderFields}
        hasExpandedAction={!driverFieldsQuery.isLoading}
      />
      <Flex className="driver-list-table" dataTestId="drivers-table-container">
        {driverFieldsQuery.isLoading && <Loading />}
        {!driverFieldsQuery.isLoading && filterBuilderFields.length > 0 && (
          <Table
            ref={tableRef}
            tableOptions={{
              selectableRows: 0,
              movableRows: true,
              movableRowsConnectedElements: '.route-item'
            }}
            headerOptions={{ showResults: true }}
            defaultData={data}
            columns={driverTableColumns}
            columnsPreference={columnsPreferences}
            pagination={{
              pageSize: 51,
              mode: 'remote',
              type: 'scroll'
            }}
            requestData={(page) => requestDataTable(page)}
            events={{
              movableRowsElementDrop: (_, element, row) =>
                handleDropDriver(row.getData(), element.dataset.routeid)
            }}
            dataTestId="drivers-table"
            noDataMessage={getResourceTo('Greenmile.Grid.NoResult')}
          />
        )}
      </Flex>
    </Container>
  );
}

export default DriverList;
